import { useMutation, useQuery } from '@tanstack/react-query';
import { canHaveParent, type NodeType } from '@goalbuddy/core';
import { toast } from 'sonner';
import {
  Archive,
  Bot,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  HelpCircle,
  Hourglass,
  Inbox,
  Plus,
  LayoutDashboard,
  LogOut,
  Map,
  Monitor,
  Moon,
  NotebookPen,
  Bookmark,
  Search,
  Settings,
  Sun,
  SunMedium,
  Zap,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Kbd,
} from '@/components/ui';
import { api } from '@/lib/api';
import { areaColor } from '@/lib/area';
import { useMe, useNodes, useStructure } from '@/lib/hooks';
import { buildTree, type TreeNode } from '@/lib/tree';
import {
  getDragged,
  getTheme,
  openNewNode,
  openPalette,
  setDragged,
  setTheme,
  type Theme,
} from '@/lib/ui-events';
import { cn } from '@/lib/utils';
import { TypeIcon } from './node-meta';

const SIDEBAR_TYPES = new Set(['area', 'goal', 'project']);
const STORAGE_KEY = 'gb.sidebar.collapsed';

function loadCollapsed(user: string): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(`${STORAGE_KEY}.${user}`) ?? '{}');
  } catch {
    return {};
  }
}

export function Logo({ size = 28 }: { size?: number }) {
  return (
    <span
      className="flex items-center justify-center rounded-[9px] bg-primary"
      style={{ width: size, height: size }}
    >
      <svg width={size * 0.62} height={size * 0.62} viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="7" stroke="white" strokeWidth="2.2" />
        <circle cx="10" cy="10" r="2.2" fill="#fbbf24" />
      </svg>
    </span>
  );
}

function NavItem({
  to,
  icon,
  children,
  end,
  count,
}: {
  to: string;
  icon: ReactNode;
  children: ReactNode;
  end?: boolean;
  count?: number;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm text-fg/80 transition-colors hover:bg-muted hover:text-fg',
          isActive && 'bg-accent font-medium text-primary hover:bg-accent hover:text-primary',
        )
      }
    >
      <span className="shrink-0 opacity-90">{icon}</span>
      <span className="flex-1 truncate">{children}</span>
      {count ? (
        <span className="rounded-full bg-primary/12 px-1.5 text-[11px] font-semibold leading-5 text-primary">
          {count}
        </span>
      ) : null}
    </NavLink>
  );
}

function Group({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="px-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-fg/80">
        {label}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function TreeItem({
  t,
  depth,
  collapsed,
  toggle,
  color,
  flat,
}: {
  flat?: boolean;
  t: TreeNode;
  depth: number;
  collapsed: Record<string, boolean>;
  toggle: (id: number) => void;
  color: string | null;
}) {
  const isCollapsed = collapsed[t.node.id] ?? depth >= 1;
  const kids = t.children;
  const [over, setOver] = useState<'ok' | 'bad' | null>(null);
  const move = useMutation({
    mutationFn: (v: { id: number; parent: number }) => api.moveNode(v.id, v.parent),
  });
  const check = () => {
    const d = getDragged();
    if (!d || d.id === t.node.id) return null;
    return canHaveParent(d.type as NodeType, t.node.type as NodeType) ? 'ok' : 'bad';
  };
  return (
    <div>
      <div className="flex items-center" style={{ paddingLeft: depth * 12 }}>
        <button
          className={cn(
            'flex h-6 w-5 items-center justify-center text-muted-fg',
            kids.length === 0 && 'invisible',
          )}
          onClick={() => toggle(t.node.id)}
          aria-label={isCollapsed ? 'Expand' : 'Collapse'}
        >
          <ChevronRight size={13} className={cn('transition-transform', !isCollapsed && 'rotate-90')} />
        </button>
        <NavLink
          to={`/node/${t.node.id}`}
          onDragOver={(e) => {
            const c = check();
            if (c) {
              e.preventDefault();
              setOver(c);
            }
          }}
          onDragLeave={() => setOver(null)}
          onDrop={(e) => {
            e.preventDefault();
            const d = getDragged();
            const c = check();
            setOver(null);
            setDragged(null);
            if (!d) return;
            if (c !== 'ok') return void toast.error(`A ${d.type} cannot go under a ${t.node.type}`);
            move.mutate(
              { id: d.id, parent: t.node.id },
              { onSuccess: () => toast.success(`Moved into ${t.node.title}`) },
            );
          }}
          className={({ isActive }) =>
            cn(
              'flex min-w-0 flex-1 items-center gap-2 rounded-lg px-1.5 py-1 text-sm transition-colors hover:bg-muted',
              isActive && 'bg-accent font-medium text-primary hover:bg-accent',
              over === 'ok' && 'bg-ok/15 ring-2 ring-ok',
              over === 'bad' && 'ring-2 ring-danger',
            )
          }
        >
          {depth === 0 && !flat ? (
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ background: color ?? 'var(--color-muted-fg)' }}
            />
          ) : (
            <TypeIcon type={t.node.type} size={13} />
          )}
          <span className="truncate">{t.node.title}</span>
        </NavLink>
      </div>
      {!isCollapsed &&
        kids.map((c) => (
          <TreeItem
            key={c.node.id}
            t={c}
            depth={depth + 1}
            collapsed={collapsed}
            toggle={toggle}
            color={color}
          />
        ))}
    </div>
  );
}

const THEME_NEXT: Record<Theme, Theme> = { system: 'light', light: 'dark', dark: 'system' };
const THEME_ICON = { system: Monitor, light: SunMedium, dark: Moon };

function ThemeButton() {
  const [theme, set] = useState<Theme>(getTheme);
  const Icon = THEME_ICON[theme];
  return (
    <button
      className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-fg hover:bg-muted hover:text-fg"
      title={`Theme: ${theme} (click to change)`}
      aria-label={`Theme: ${theme}`}
      onClick={() => {
        const next = THEME_NEXT[theme];
        setTheme(next);
        set(next);
      }}
    >
      <Icon size={16} />
    </button>
  );
}

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { data: me } = useMe();
  const { data: nodes } = useStructure();
  const { data: views } = useQuery({ queryKey: ['views'], queryFn: api.views });
  const { data: inbox } = useNodes({ parent: 'none', type: ['task', 'idea'] });
  const { data: questions } = useQuery({ queryKey: ['questions'], queryFn: api.questions });
  const navigate = useNavigate();
  const user = me?.name ?? 'anon';
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => loadCollapsed(user));
  const toggle = (id: number) => {
    const depthDefault = !(nodes?.find((n) => n.id === id)?.parent_id === null);
    const next = { ...collapsed, [id]: !(collapsed[id] ?? depthDefault) };
    setCollapsed(next);
    try {
      localStorage.setItem(`${STORAGE_KEY}.${user}`, JSON.stringify(next));
    } catch {
      /* storage unavailable */
    }
  };
  const lists = (nodes ?? []).filter((n) => n.type === 'list').sort((a, b) => a.title.localeCompare(b.title));
  const tree = buildTree(nodes ?? [], (n) => SIDEBAR_TYPES.has(n.type));
  const builtIn = new Set(['Today', 'Next actions', 'Inbox', 'Waiting for', 'Agent tasks', 'Questions']);
  const customViews = (views ?? []).filter((v) => !builtIn.has(v.name) && v.pinned);
  const waitingView = views?.find((v) => v.name === 'Waiting for');

  return (
    <nav
      className="flex h-full flex-col gap-5 overflow-y-auto p-3"
      onClick={(e) => (e.target as HTMLElement).closest('a') && onNavigate?.()}
    >
      <div className="flex items-center gap-2.5 px-1.5 pt-1 text-base font-semibold tracking-tight">
        <Logo /> GoalBuddy
      </div>
      <button
        onClick={() => {
          openPalette();
          onNavigate?.();
        }}
        className="flex h-9 items-center gap-2 rounded-lg border border-border bg-surface px-2.5 text-sm text-muted-fg shadow-[var(--shadow-card)] transition-colors hover:border-muted-fg/40"
      >
        <Search size={14} /> <span className="flex-1 text-left">Search or jump to…</span> <Kbd>⌘K</Kbd>
      </button>

      <Group label="Daily">
        <NavItem to="/" end icon={<Sun size={16} />}>
          Today
        </NavItem>
        <NavItem to="/inbox" icon={<Inbox size={16} />} count={inbox?.length}>
          Inbox
        </NavItem>
        <NavItem to="/next" icon={<Zap size={16} />}>
          Next actions
        </NavItem>
        <NavItem to="/calendar" icon={<CalendarDays size={16} />}>
          Calendar
        </NavItem>
      </Group>

      <Group label="Review">
        <NavItem to="/review/daily" icon={<NotebookPen size={16} />}>
          Daily prep
        </NavItem>
        <NavItem to="/review/weekly" icon={<NotebookPen size={16} />}>
          Weekly review
        </NavItem>
        <NavItem to="/done" icon={<CheckCircle2 size={16} />}>
          Done
        </NavItem>
        <NavItem to="/overview" icon={<LayoutDashboard size={16} />}>
          Overview
        </NavItem>
      </Group>

      <Group label="Agent">
        <NavItem to="/agents" icon={<Bot size={16} />}>
          Agent tasks
        </NavItem>
        <NavItem to="/questions" icon={<HelpCircle size={16} />} count={questions?.length}>
          Questions
        </NavItem>
        {waitingView && (
          <NavItem to={`/view/${waitingView.id}`} icon={<Hourglass size={16} />}>
            Waiting for
          </NavItem>
        )}
      </Group>

      {customViews.length > 0 && (
        <Group label="Saved views">
          {customViews.map((v) => (
            <NavItem key={v.id} to={`/view/${v.id}`} icon={<Bookmark size={16} />}>
              {v.name}
            </NavItem>
          ))}
        </Group>
      )}

      <div>
        <div className="flex items-center justify-between pb-1 pr-1">
          <NavLink
            to="/lists"
            end
            className="rounded-md px-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-fg/80 hover:text-fg"
          >
            Lists
          </NavLink>
          <button
            className="flex h-6 w-6 items-center justify-center rounded-md text-muted-fg hover:bg-muted hover:text-fg"
            aria-label="New list"
            title="New list"
            onClick={() => openNewNode('list')}
          >
            <Plus size={14} />
          </button>
        </div>
        {lists.length === 0 && <div className="px-2.5 py-1 text-xs text-muted-fg">No lists yet</div>}
        {lists.map((l) => (
          <TreeItem
            key={l.id}
            t={{ node: l, children: [] }}
            depth={0}
            flat
            collapsed={collapsed}
            toggle={toggle}
            color={areaColor(nodes, l.id)}
          />
        ))}
      </div>

      <div className="flex-1">
        <div className="flex items-center justify-between pb-1 pr-1">
          <div className="px-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-fg/80">
            Areas and goals
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex h-6 w-6 items-center justify-center rounded-md text-muted-fg hover:bg-muted hover:text-fg"
                aria-label="Create area, goal or list"
                title="New area, goal or list"
              >
                <Plus size={14} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => openNewNode('area')}>New area</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => openNewNode('goal')}>New goal</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => openNewNode('list')}>New list</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {tree.map((t) => (
          <TreeItem
            key={t.node.id}
            t={t}
            depth={0}
            collapsed={collapsed}
            toggle={toggle}
            color={areaColor(nodes, t.node.id)}
          />
        ))}
      </div>

      <div className="space-y-0.5 border-t border-border pt-2">
        <NavItem to="/map" icon={<Map size={16} />}>
          Map
        </NavItem>
        <NavItem to="/archive" icon={<Archive size={16} />}>
          Archive
        </NavItem>
        <NavItem to="/settings" icon={<Settings size={16} />}>
          Settings
        </NavItem>
        <div className="flex items-center justify-between pt-1">
          <span className="flex items-center gap-2 px-2.5 text-sm text-muted-fg">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent text-xs font-semibold text-primary">
              {(me?.name ?? '?').slice(0, 1).toUpperCase()}
            </span>
            {me?.name}
          </span>
          <span className="flex">
            <ThemeButton />
            <button
              className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-fg hover:bg-muted hover:text-fg"
              aria-label="Log out"
              title="Log out"
              onClick={async () => {
                await api.logout().catch(() => {});
                navigate('/login');
              }}
            >
              <LogOut size={16} />
            </button>
          </span>
        </div>
      </div>
    </nav>
  );
}
